import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { IndividualConfig } from 'ngx-toastr';
import { take } from 'rxjs/operators';

@Injectable()
export class NotificationsService {
    private readonly cmsToastConfig: Partial<IndividualConfig> = {
        toastClass: 'ngx-toastr cms-toast',
        positionClass: 'toast-cms-top',
        timeOut: 2600,
        extendedTimeOut: 2600,
        closeButton: false,
        tapToDismiss: true,
        progressBar: false,
        titleClass: 'cms-toast-title',
        enableHtml: false,
    };

    constructor(private toastr: ToastrService) { }

    private showCmsToast(message: string): void {
        if (!message?.trim()) {
            return;
        }

        this.toastr.clear();
        this.toastr.show(message, '', this.cmsToastConfig, '');
    }

    public success(title: string, message: string): void {
        if (!title?.trim()) {
            this.showCmsToast(message);
            return;
        }

        this.toastr.success(message, title);
    }

    public warning(title: string, message: string): void {
        if (!title?.trim()) {
            this.showCmsToast(message);
            return;
        }

        this.toastr.warning(message, title);
    }

    public error(title: string, message: string): void {
        if (!title?.trim()) {
            this.showCmsToast(message);
            return;
        }

        this.toastr.error(message, title);
    }

    public successWithAction(
        message: string,
        actionLabel: string,
        onAction: () => void,
        options: Partial<IndividualConfig> = {}
    ) {
        this.toastr.clear();
        const toast = this.toastr.show(
            `<span class="cms-toast-text">${this.escapeHtml(message)}</span>`
            + `<span role="button" tabindex="0" class="cms-toast-action">${this.escapeHtml(actionLabel)}</span>`,
            '',
            {
                ...this.cmsToastConfig,
                enableHtml: true,
                timeOut: 7000,
                extendedTimeOut: 7000,
                tapToDismiss: false,
                toastClass: 'ngx-toastr cms-toast cms-toast--action',
                ...options,
            },
            '',
        );

        const attachAction = (): void => {
            const root = toast.portal?.location?.nativeElement as HTMLElement | undefined;
            const actionEl = root?.querySelector<HTMLElement>('.cms-toast-action');
            if (!actionEl || actionEl.dataset.cmsToastActionBound === 'true') {
                return;
            }

            actionEl.dataset.cmsToastActionBound = 'true';

            const run = (event: Event): void => {
                event.preventDefault();
                event.stopPropagation();
                this.toastr.clear(toast.toastId);
                onAction();
            };

            actionEl.addEventListener('click', run, { once: true });
            actionEl.addEventListener('keydown', (event: KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    run(event);
                }
            }, { once: true });
        };

        toast.onShown.pipe(take(1)).subscribe(() => attachAction());
        setTimeout(() => attachAction(), 0);

        return toast;
    }

    private escapeHtml(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }
}
